/**
 * Vérifie si un champ est vide ou contient uniquement des espaces ou \n
 */
export const isEmptyField = (value: any): boolean => {
  if (value === undefined || value === null) return true;
  if (typeof value === 'string') return value.trim() === '' || value.trim() === '\n';
  if (typeof value === 'number') return isNaN(value);
  return true;
};

/**
 * Détermine si on doit afficher le temps en voiture plutôt qu'à pied
 */
export const shouldShowDriveTime = (
  walkTime: string | undefined,
  limitWalk: string | number | undefined
): boolean => {
  if (isEmptyField(walkTime)) return false;
  
  const walkMinutes = parseInt(String(walkTime), 10);
  const limit = parseInt(String(limitWalk), 10);
  
  if (isNaN(walkMinutes) || isNaN(limit)) return false;
  
  return walkMinutes > limit;
};

/**
 * Détermine quel temps de trajet afficher en fonction des valeurs et de la limite
 * @param walkTime Temps à pied en minutes
 * @param bikeTime Temps en vélo en minutes
 * @param driveTime Temps en voiture en minutes
 * @param limitWalk Limite de temps de marche en minutes
 * @returns Le type de trajet à afficher: 'walk', 'bike', 'drive' ou null si aucun
 */
export const getDisplayTravelType = (
  timewalk: string | number | undefined,
  timebike: string | number | undefined,
  timedrive: string | number | undefined,
  limitWalk: string | number | undefined
): 'walk' | 'bike' | 'drive' | null => {
  // Convertir en nombres
  const walkMinutes = parseInt(String(timewalk), 10);
  const bikeMinutes = parseInt(String(timebike), 10);
  const driveMinutes = parseInt(String(timedrive), 10);
  const limit = parseInt(String(limitWalk), 10);
  
  // Logs pour le débogage
  console.log('getDisplayTravelType - Input values:', {
    timewalk, timebike, timedrive, limitWalk,
    walkMinutes, bikeMinutes, driveMinutes, limit,
    isWalkValid: !isNaN(walkMinutes) && walkMinutes > 0,
    isBikeValid: !isNaN(bikeMinutes) && bikeMinutes > 0,
    isDriveValid: !isNaN(driveMinutes) && driveMinutes > 0,
    isLimitValid: !isNaN(limit)
  });
  
  // Vérifier les valeurs invalides
  if (isNaN(limit)) {
    console.log('getDisplayTravelType - Invalid limit, returning null');
    return null;
  }
  
  // Appliquer la logique:
  // - Si timewalk < limit_walk: afficher le temps à pied
  if (!isNaN(walkMinutes) && walkMinutes > 0 && walkMinutes < limit) {
    console.log('getDisplayTravelType - Using walk time (under limit)');
    return 'walk';
  }
  
  // - Si timewalk > limit_walk et timebike < limit_walk: afficher le temps en vélo
  if (!isNaN(walkMinutes) && walkMinutes >= limit && 
      !isNaN(bikeMinutes) && bikeMinutes > 0 && bikeMinutes < limit) {
    console.log('getDisplayTravelType - Using bike time (walk over limit, bike under limit)');
    return 'bike';
  }
  
  // - Si timewalk > limit_walk et timebike > limit_walk: afficher le temps en voiture
  if (!isNaN(walkMinutes) && walkMinutes >= limit && 
      (!isNaN(bikeMinutes) && bikeMinutes >= limit) && 
      !isNaN(driveMinutes) && driveMinutes > 0) {
    console.log('getDisplayTravelType - Using drive time (walk and bike over limit)');
    return 'drive';
  }
  
  // Fallback: si on a un temps de marche, l'afficher
  if (!isNaN(walkMinutes) && walkMinutes > 0) {
    console.log('getDisplayTravelType - Fallback to walk time');
    return 'walk';
  }
  
  // Aucun temps de trajet valide
  console.log('getDisplayTravelType - No valid travel time, returning null');
  return null;
};

/**
 * Formate une durée en minutes en format lisible
 */
export const formatDuration = (minutes: string | undefined): string => {
  if (isEmptyField(minutes)) return '';
  
  const mins = parseInt(String(minutes), 10);
  if (isNaN(mins)) return '';
  
  if (mins < 60) return `${mins}min`;
  
  const hours = Math.floor(mins / 60);
  const remainingMins = mins % 60;
  
  if (remainingMins === 0) return `${hours}h`;
  return `${hours}h${remainingMins}min`;
};